import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { store } from './sprout-store';
import type {
  GroundControlLocation,
  Inspection,
  Concern,
  DraftInspection,
  DraftConcern,
} from './types';
import { SEED_INSPECTIONS, SEED_CONCERNS } from './data/seedData';
import { calculateNextInspectionDate } from './utils/scheduling';

// ── Page type ─────────────────────────────────────────────────────────────────
export type Page = 'audit-menu' | 'inspection-form' | 'dashboard';

// ── Context value type ────────────────────────────────────────────────────────
interface AppContextValue {
  currentPage: Page;
  navigate: (page: Page) => void;
  isOnline: boolean;
  showSplash: boolean;
  dismissSplash: () => void;
  selectedLocation: GroundControlLocation | null;
  setSelectedLocation: (loc: GroundControlLocation | null) => void;
  // locations is derived from inspections + concerns — always up-to-date
  locations: GroundControlLocation[];
  inspections: Inspection[];
  concerns: Concern[];
  isLoading: boolean;
  lastRefresh: Date | null;
  refreshData: () => Promise<void>;
  createInspection: (insp: Omit<Inspection, 'id'>) => Promise<void>;
  createConcern: (concern: Omit<Concern, 'id'>) => Promise<void>;
  drafts: DraftInspection[];
  saveDraft: (draft: DraftInspection) => void;
  deleteDraft: (localId: string) => void;
  clearAllDrafts: () => void;
  uploadDraft: (draft: DraftInspection) => Promise<void>;
  draftConcerns: DraftConcern[];
  saveDraftConcern: (concern: DraftConcern) => void;
  clearDraftConcernsForDraft: (parentDraftId: string) => void;
  currentUser: string;
  currentSupervisor: string;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  const [currentPage, setCurrentPage] = useState<Page>('audit-menu');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showSplash, setShowSplash] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<GroundControlLocation | null>(null);

  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [concerns, setConcerns] = useState<Concern[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const [drafts, setDrafts] = useState<DraftInspection[]>([]);
  const [draftConcerns, setDraftConcerns] = useState<DraftConcern[]>([]);

  const splashShownRef = useRef(false);
  const draftConcernsRef = useRef<DraftConcern[]>([]);
  useEffect(() => { draftConcernsRef.current = draftConcerns; }, [draftConcerns]);

  const currentUser = 'Current User';
  const currentSupervisor = 'Mike Wilson';

  // ── Derive locations live from inspections + concerns ─────────────────────
  // No separate cgc_locations collection needed — always consistent.
  const locations = useMemo((): GroundControlLocation[] => {
    const map = new Map<string, Inspection[]>();
    for (const insp of inspections) {
      const key = `${insp.Area}|||${insp.AreaType}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(insp);
    }

    return Array.from(map.entries())
      .map(([key, insps]) => {
        const [area, areaType] = key.split('|||');
        const sorted = [...insps].sort((a, b) =>
          b.DateInspected.localeCompare(a.DateInspected)
        );
        const latest = sorted[0];
        const openConcerns = concerns.filter(
          (c) => c.Area === area && c.AreaType === areaType && c.Status !== 'Closed'
        ).length;
        return {
          Area: area,
          AreaType: areaType,
          LastInspectedDate: latest.DateInspected,
          InspectedBy: latest.InspectedBy,
          NumberOfConcerns: openConcerns,
          AllRedTaped: latest.RedTaped,
          NextInspectionDueBy: calculateNextInspectionDate(latest.DateInspected),
          Comment: latest.Comment,
        } as GroundControlLocation;
      })
      .sort((a, b) => a.Area.localeCompare(b.Area));
  }, [inspections, concerns]);

  // ── Online/offline detection ───────────────────────────────────────────────
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // ── Load drafts from localStorage ─────────────────────────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem('cgc_drafts');
      if (raw) setDrafts(JSON.parse(raw));
      const rawConcerns = localStorage.getItem('cgc_draft_concerns');
      if (rawConcerns) setDraftConcerns(JSON.parse(rawConcerns));
    } catch {
      // ignore parse errors
    }
  }, []);

  // ── Load data from sprout-store (inspections + concerns only) ─────────────
  const refreshData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [inspRecords, concernRecords] = await Promise.all([
        store.list('cgc_inspections'),
        store.list('cgc_concerns'),
      ]);

      if (inspRecords.length === 0) {
        const seeded = await Promise.all(
          SEED_INSPECTIONS.map((insp) => store.create('cgc_inspections', insp))
        );
        setInspections(seeded.map((r) => ({ ...(r.data as Inspection), id: r.id })));
      } else {
        setInspections(inspRecords.map((r) => ({ ...(r.data as Inspection), id: r.id })));
      }

      if (concernRecords.length === 0) {
        const seeded = await Promise.all(
          SEED_CONCERNS.map((c) => store.create('cgc_concerns', c))
        );
        setConcerns(seeded.map((r) => ({ ...(r.data as Concern), id: r.id })));
      } else {
        setConcerns(concernRecords.map((r) => ({ ...(r.data as Concern), id: r.id })));
      }

      setLastRefresh(new Date());
    } catch {
      // Use empty state if store unavailable
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const navigate = useCallback((page: Page) => {
    if (page === 'dashboard' && !splashShownRef.current) {
      setShowSplash(true);
      splashShownRef.current = true;
      setTimeout(() => setShowSplash(false), 9000);
    }
    setCurrentPage(page);
  }, []);

  const dismissSplash = useCallback(() => setShowSplash(false), []);

  // ── Inspection mutations ───────────────────────────────────────────────────
  const createInspection = useCallback(async (insp: Omit<Inspection, 'id'>) => {
    try {
      const rec = await store.create('cgc_inspections', insp);
      setInspections((prev) => [...prev, { ...(rec.data as Inspection), id: rec.id }]);
    } catch {
      alert('Could not save inspection.');
    }
  }, []);

  // ── Concern mutations ──────────────────────────────────────────────────────
  const createConcern = useCallback(async (concern: Omit<Concern, 'id'>) => {
    try {
      const rec = await store.create('cgc_concerns', concern);
      setConcerns((prev) => [...prev, { ...(rec.data as Concern), id: rec.id }]);
    } catch {
      alert('Could not save concern.');
    }
  }, []);

  // ── Draft management (localStorage) ───────────────────────────────────────
  const saveDraft = useCallback((draft: DraftInspection) => {
    setDrafts((prev) => {
      const updated = [...prev.filter((d) => d.localId !== draft.localId), draft];
      localStorage.setItem('cgc_drafts', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const deleteDraft = useCallback((localId: string) => {
    setDrafts((prev) => {
      const updated = prev.filter((d) => d.localId !== localId);
      localStorage.setItem('cgc_drafts', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearAllDrafts = useCallback(() => {
    setDrafts([]);
    setDraftConcerns([]);
    localStorage.removeItem('cgc_drafts');
    localStorage.removeItem('cgc_draft_concerns');
  }, []);

  const uploadDraft = useCallback(async (draft: DraftInspection) => {
    try {
      const inspRec = await store.create('cgc_inspections', {
        Area: draft.Area,
        AreaType: draft.AreaType,
        DateInspected: draft.DateInspected,
        InspectedBy: draft.InspectedBy,
        Supervisor: draft.Supervisor,
        RedTaped: draft.RedTaped,
        HighbackInspected: draft.HighbackInspected,
        NumberOfConcerns: draft.NumberOfConcerns,
        Comment: draft.Comment,
        Year: draft.Year,
        Period: draft.Period,
      } as Inspection);
      setInspections((prev) => [...prev, { ...(inspRec.data as Inspection), id: inspRec.id }]);

      // Upload related draft concerns
      const relatedConcerns = draftConcernsRef.current.filter(
        (c) => c.parentDraftId === draft.localId
      );
      for (const dc of relatedConcerns) {
        const cRec = await store.create('cgc_concerns', {
          Area: dc.Area, AreaType: dc.AreaType, Deficiency: dc.Deficiency,
          RemedialAction: dc.RemedialAction, SpadBT: dc.SpadBT,
          Remediated: dc.Remediated, RemediatedDate: dc.RemediatedDate,
          Comment: dc.Comment, Inspector: dc.Inspector, Supervisor: dc.Supervisor,
          InspectionDate: dc.InspectionDate, Status: dc.Status,
        } as Concern);
        setConcerns((prev) => [...prev, { ...(cRec.data as Concern), id: cRec.id }]);
      }

      // Location data auto-updates via the useMemo — no manual patch needed
      deleteDraft(draft.localId);
    } catch {
      alert('Could not upload draft.');
    }
  }, [deleteDraft]);

  // ── Draft concern management ───────────────────────────────────────────────
  const saveDraftConcern = useCallback((concern: DraftConcern) => {
    setDraftConcerns((prev) => {
      const updated = [...prev.filter((c) => c.localId !== concern.localId), concern];
      localStorage.setItem('cgc_draft_concerns', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const clearDraftConcernsForDraft = useCallback((parentDraftId: string) => {
    setDraftConcerns((prev) => {
      const updated = prev.filter((c) => c.parentDraftId !== parentDraftId);
      localStorage.setItem('cgc_draft_concerns', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AppContext.Provider
      value={{
        currentPage, navigate, isOnline, showSplash, dismissSplash,
        selectedLocation, setSelectedLocation,
        locations, inspections, concerns, isLoading, lastRefresh, refreshData,
        createInspection, createConcern,
        drafts, saveDraft, deleteDraft, clearAllDrafts, uploadDraft,
        draftConcerns, saveDraftConcern, clearDraftConcernsForDraft,
        currentUser, currentSupervisor,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
